import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Car, User, Phone, CreditCard, X, Check, MapPin, Hash } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFlashMessage } from '../components/FlashMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import { vehiclesAPI, handleApiError } from '../services/api';
import { formatPhoneNumber, cleanPhoneNumber } from '../utils/phoneFormatter';
import { carBrands } from '../data/carBrands';
import { FormSelect } from '../components/FormSelect';
import { FormCombobox } from '../components/FormCombobox';

const VehicleForm = () => {
  const navigate = useNavigate();
  const { user, getUserLocations } = useAuth();
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [isLoading, setIsLoading] = useState(false);

  const locations = getUserLocations();

  // Türkçe karakterleri destekleyen kapitalizasyon fonksiyonu
  const capitalizeWithTurkish = (text) => {
    const turkishMap = {
      'ç': 'Ç', 'ğ': 'Ğ', 'ı': 'İ', 'ö': 'Ö', 'ş': 'Ş', 'ü': 'Ü'
    };
    return text.replace(/(^|\s)[\wçğıöşüÇĞIİÖŞÜ]/g, letter => {
      // Eğer boşluk + harf ise, boşluğu koru
      if (letter.startsWith(' ')) {
        return ' ' + (turkishMap[letter.trim()] || letter.trim().toUpperCase());
      }
      return turkishMap[letter] || letter.toUpperCase();
    });
  };

  // Get initial default amount if single location
  const getInitialAmount = () => {
    if (locations.length === 1 && locations[0].default_valet_price) {
      const price = parseFloat(locations[0].default_valet_price);
      if (!isNaN(price) && price > 0) {
        return price.toFixed(0);
      }
    }
    return '';
  };

  const [formData, setFormData] = useState({
    plate: '',
    brand: '',
    reference_code: '',
    customer_name: '',
    customer_phone: '',
    service_type: 'valet',
    amount: getInitialAmount(),
    location_id: locations.length === 1 ? locations[0].id.toString() : '',
    notes: '',
    payment_status: 'pending',
    parking_spot: ''
  });

  const [errors, setErrors] = useState({});
  const [plateSearching, setPlateSearching] = useState(false);

  // Location seçildiğinde veya değiştiğinde default amount'u set et
  useEffect(() => {
    if (formData.location_id) {
      const selectedLocation = locations.find(loc => loc.id === parseInt(formData.location_id));

      if (selectedLocation?.default_valet_price) {
        const price = parseFloat(selectedLocation.default_valet_price);

        if (!isNaN(price) && price > 0) {
          const newAmount = price.toFixed(0);

          setFormData(prev => {
            // Sadece amount boşsa veya 0 ise güncelle
            if (!prev.amount || prev.amount === '0' || prev.amount === 0) {
              return { ...prev, amount: newAmount };
            }
            return prev;
          });
        }
      }
    }
  }, [formData.location_id, locations]);

  // Plaka ile önceki müşteri bilgilerini getir - SADECE KENDI LOKASYONLARDA
  const searchCustomerByPlate = async (plateNumber) => {
    if (!plateNumber || plateNumber.length < 5) return;

    // Lokasyon seçilmemişse önce uygun lokasyonu kullan
    let targetLocationId = formData.location_id;

    if (!targetLocationId) {
      // Eğer tek lokasyon varsa otomatik seç
      if (locations.length === 1) {
        targetLocationId = locations[0].id;
        setFormData(prev => ({ ...prev, location_id: targetLocationId }));
      } else {
        // Çoklu lokasyon durumunda tüm lokasyonlarda ara
        console.log('Çoklu lokasyon tespit edildi, tüm lokasyonlarda aranıyor...');
        setPlateSearching(true);

        for (const location of locations) {
          try {
            const response = await vehiclesAPI.searchByPlateInLocation(plateNumber, location.id);
            if (response.data.success && response.data.data) {
              const customer = response.data.data;
              console.log(`Müşteri ${location.name} lokasyonunda bulundu:`, customer);

              // Set default amount from found location
              let defaultAmount = '';
              if (location.default_valet_price && location.default_valet_price !== '0.00') {
                const price = parseFloat(location.default_valet_price);
                if (!isNaN(price) && price > 0) {
                  defaultAmount = price.toFixed(0);
                }
              }

              setFormData(prev => ({
                ...prev,
                location_id: location.id,
                customer_name: customer.customer_name || '',
                customer_phone: customer.customer_phone ? formatPhoneNumber(customer.customer_phone) : '',
                brand: customer.brand || '',
                amount: defaultAmount || prev.amount
              }));

              showSuccess(`${plateNumber} plakası ${location.name} lokasyonunda bulundu: ${customer.customer_name || 'Bilinmeyen'}`);
              setPlateSearching(false);
              return;
            }
          } catch (error) {
            console.error(`Lokasyon ${location.name} araması hatası:`, error);
          }
        }

        console.log('Plaka hiçbir lokasyonda bulunamadı');
        setPlateSearching(false);
        return;
      }
    }

    setPlateSearching(true);
    try {
      // Güvenlik: Sadece seçili lokasyonda arama yap
      const response = await vehiclesAPI.searchByPlateInLocation(plateNumber, targetLocationId);

      if (response.data.success && response.data.data) {
        const customer = response.data.data;
        console.log('Müşteri bilgileri bulundu:', customer);
        setFormData(prev => ({
          ...prev,
          customer_name: customer.customer_name || '',
          customer_phone: customer.customer_phone ? formatPhoneNumber(customer.customer_phone) : '',
          brand: customer.brand || ''
        }));
        showSuccess(`${plateNumber} plakası için müşteri bilgileri getirildi: ${customer.customer_name || 'Bilinmeyen'}`);
      } else {
        console.log('Bu plaka için müşteri bilgisi bulunamadı:', plateNumber);
      }

    } catch (error) {
      console.error('Müşteri arama hatası:', error);
      // Hata durumunda da sessizce devam et, kullanıcıyı rahatsız etme
    } finally {
      setPlateSearching(false);
    }
  };

  // Müşteri adını temizle
  const clearCustomerName = () => {
    setFormData(prev => ({
      ...prev,
      customer_name: '',
      customer_phone: ''
    }));
    // Hata mesajını da temizle
    if (errors.customer_name) {
      setErrors(prev => ({ ...prev, customer_name: '' }));
    }
    if (errors.customer_phone) {
      setErrors(prev => ({ ...prev, customer_phone: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.plate.trim()) {
      newErrors.plate = 'Plaka numarası gereklidir';
    }

    // Müşteri adı opsiyonel - sadece girildiyse kontrol et
    if (formData.customer_name.trim() && formData.customer_name.trim().length < 2) {
      newErrors.customer_name = 'Müşteri adı en az 2 karakter olmalıdır';
    }

    // Telefon numarası opsiyonel - sadece girildiyse format kontrol et
    if (formData.customer_phone.trim() && !/^[0-9+\-\s]+$/.test(formData.customer_phone)) {
      newErrors.customer_phone = 'Geçerli bir telefon numarası giriniz';
    }

    if (formData.amount.trim() && (isNaN(Number(formData.amount)) || Number(formData.amount) < 0 || !Number.isInteger(Number(formData.amount)))) {
      newErrors.amount = 'Geçerli bir tam sayı tutar giriniz';
    }

    if (!formData.location_id) {
      newErrors.location_id = 'Lokasyon seçimi gereklidir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'customer_phone') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else if (name === 'plate') {
      // Plaka girişinde boşluk karakterini kaldır ve büyük harfe çevir
      const cleanedValue = value.replace(/\s/g, '').toUpperCase();
      setFormData(prev => ({
        ...prev,
        [name]: cleanedValue
      }));

      // Plaka uzunluğu yeterliyse müşteri bilgilerini ara
      if (cleanedValue.length >= 7) {
        searchCustomerByPlate(cleanedValue);
      }
    } else if (name === 'brand') {
      // Marka seçimi
      setFormData(prev => ({
        ...prev,
        brand: value
      }));
    } else if (name === 'reference_code') {
      // Sadece rakam kabul et (1-999 arası)
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue === '' || (parseInt(numericValue) >= 1 && parseInt(numericValue) <= 999)) {
        setFormData(prev => ({
          ...prev,
          [name]: numericValue
        }));
      }
    } else if (name === 'customer_name') {
      // Müşteri adında her kelimenin ilk harfini büyük yap (Türkçe karakterler dahil)
      const capitalizedValue = capitalizeWithTurkish(value);
      setFormData(prev => ({
        ...prev,
        [name]: capitalizedValue
      }));
    } else if (name === 'location_id') {
      // Lokasyon seçildiğinde hizmet türünü otomatik ayarla
      const selectedLocation = locations.find(loc => loc.id === parseInt(value));
      let defaultServiceType = 'valet';
      let defaultAmount = '';

      if (selectedLocation?.type === 'wash') {
        defaultServiceType = 'wash';
      } else if (selectedLocation?.type === 'both') {
        defaultServiceType = 'valet';
      }

      // Set default amount from location's default_valet_price
      if (selectedLocation?.default_valet_price && selectedLocation.default_valet_price !== '0.00') {
        const price = parseFloat(selectedLocation.default_valet_price);
        if (!isNaN(price) && price > 0) {
          defaultAmount = price.toFixed(0);
        }
      }

      setFormData(prev => ({
        ...prev,
        [name]: value,
        service_type: defaultServiceType,
        amount: defaultAmount
      }));

      // Lokasyon değiştiğinde plaka tekrar aranırsa güvenlik için müşteri bilgilerini temizle
      if (formData.plate.length >= 7) {
        // Yeni lokasyonda plaka araması yap
        const currentPlate = formData.plate;
        setTimeout(() => {
          searchCustomerByPlate(currentPlate);
        }, 100);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showDanger('Lütfen tüm gerekli alanları doldurun');
      return;
    }

    setIsLoading(true);

    try {
      // Boş alanlar için default değerleri uygula
      const customerName = formData.customer_name.trim() || 'Veribil';
      const customerPhone = cleanPhoneNumber(formData.customer_phone) || '00000000000';

      // Referans kodu: Sadece rakam girildiyse CS prefix ekle
      let referenceCode = null;
      if (formData.reference_code.trim()) {
        referenceCode = 'CS' + formData.reference_code.trim();
      }

      // Backend API'sinin beklediği format
      const apiData = {
        location_id: parseInt(formData.location_id),
        plate_number: formData.plate,
        brand: formData.brand || null,
        model: null, // Model artık kullanılmıyor
        reference_code: referenceCode,
        customer_name: customerName,
        customer_phone: customerPhone,
        service_type: formData.service_type,
        displayed_price: formData.amount === '' ? 0 : parseInt(formData.amount),
        parking_note: formData.notes || '',
        payment_status: formData.payment_status,
        parking_spot: formData.parking_spot.trim() || null
      };

      console.log('Submitting vehicle data:', apiData);
      const response = await vehiclesAPI.checkinVehicle(apiData);

      if (response.data.success) {
        const responseData = response.data.data;
        const transactionId = responseData?.transaction_id;
        const referenceNumber = responseData?.reference_number;
        const referenceCode = referenceNumber ? `CS${referenceNumber}` : `V${transactionId?.toString().padStart(4, '0')}`;

        showSuccess(`${formData.plate} plakası ile ${customerName} araç girişi başarıyla kaydedildi. Referans No: ${referenceCode}. Müşteriye sözlü olarak bildirilebilir.`);
        console.log('Vehicle check-in response:', responseData);

        // Navigate to vehicle status page to show reference number
        if (transactionId) {
          navigate(`/vehicles/status/${transactionId}`);
        } else {
          navigate('/vehicles');
        }
      } else {
        throw new Error(response.data.message || 'Kayıt başarısız');
      }

    } catch (error) {
      console.error('Vehicle checkin error:', error);
      const errorMessage = handleApiError(error);
      showDanger(`Araç girişi kaydedilirken hata oluştu: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Staff yetkisi kontrolü - SADECE STAFF ERİŞEBİLİR
  const hasAccess = user && (
    user.isStaff ||
    user.user_type === 'staff'
  );

  if (!hasAccess) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
        <p className="text-gray-500 text-sm">Kullanıcı türü: {user?.user_type}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FlashContainer />

      {/* Submit Buttons - At Top */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={() => navigate('/vehicles')}
          className="btn-secondary"
          disabled={isLoading}
        >
          İptal
        </button>
        <button
          onClick={handleSubmit}
          className="btn-primary flex items-center"
          disabled={isLoading}
        >
          {isLoading ? (
            <LoadingSpinner size="sm" className="mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Araç Girişi Kaydet
        </button>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* PRIMARY FIELDS - Level 1 (Emphasized with background) */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 space-y-6">
              {/* Plate Number - Large, Primary */}
              <div>
                <label className="block text-base font-semibold text-gray-900 mb-3">
                  <Car className="w-5 h-5 inline mr-2 text-[#7a2c2c]" />
                  Plaka Numarası *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="plate"
                    value={formData.plate}
                    onChange={handleInputChange}
                    className={`form-input text-lg h-14 bg-white ${errors.plate ? 'border-red-500' : ''} ${plateSearching ? 'pr-10' : ''}`}
                    placeholder="06ABC123"
                    disabled={isLoading}
                  />
                  {plateSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <LoadingSpinner size="sm" />
                    </div>
                  )}
                </div>
                {errors.plate && (
                  <p className="text-red-500 text-sm mt-1">{errors.plate}</p>
                )}
              </div>

              {/* Reference Code & Amount Row - Primary Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-base font-semibold text-gray-900 mb-3">
                    <Hash className="w-5 h-5 inline mr-2 text-[#7a2c2c]" />
                    Referans Kodu
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#7a2c2c] font-semibold text-lg">
                      CS
                    </span>
                    <input
                      type="text"
                      name="reference_code"
                      value={formData.reference_code}
                      onChange={handleInputChange}
                      className="form-input text-lg h-14 pl-16 bg-white"
                      placeholder="1-999 (otomatik)"
                      maxLength={3}
                      disabled={isLoading}
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Boş bırakılırsa otomatik atanır
                  </p>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-900 mb-3">
                    <CreditCard className="w-5 h-5 inline mr-2 text-[#7a2c2c]" />
                    Tutar (TL) *
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    className={`form-input text-lg h-14 bg-white ${errors.amount ? 'border-red-500' : ''}`}
                    placeholder="0"
                    min="0"
                    step="1"
                    disabled={isLoading}
                  />
                  {errors.amount && (
                    <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t-2 border-gray-300 pt-8">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-6 flex items-center">
                <span className="bg-gray-100 px-3 py-1 rounded">Ek Bilgiler</span>
              </h3>

              {/* SECONDARY FIELDS - Level 2 (Standard) */}
              <div className="space-y-6">
                {/* Customer Name & Phone Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-2 text-[#7a2c2c]" />
                      Müşteri Adı
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleInputChange}
                        className={`form-input ${errors.customer_name ? 'border-red-500' : ''} ${formData.customer_name ? 'pr-10' : ''}`}
                        placeholder="Müşteri adı (isteğe bağlı)"
                        disabled={isLoading}
                      />
                      {formData.customer_name && (
                        <button
                          type="button"
                          onClick={clearCustomerName}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Müşteri bilgilerini temizle"
                          disabled={isLoading}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {errors.customer_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.customer_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-4 h-4 inline mr-2 text-[#7a2c2c]" />
                      Telefon Numarası
                    </label>
                    <input
                      type="tel"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleInputChange}
                      className={`form-input ${errors.customer_phone ? 'border-red-500' : ''}`}
                      placeholder="0XXX XXX XX XX (isteğe bağlı)"
                      disabled={isLoading}
                    />
                    {errors.customer_phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.customer_phone}</p>
                    )}
                  </div>
                </div>

                {/* Brand */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Car className="w-4 h-4 inline mr-2 text-[#7a2c2c]" />
                    Araç Markası
                  </label>
                  <FormCombobox
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    options={carBrands.map(brand => ({ value: brand.name, label: brand.name }))}
                    placeholder="Marka ara ve seçin"
                    searchPlaceholder="Marka adı yazın..."
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* Service Type & Location Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Seçilen lokasyonun türüne göre hizmet türü alanını göster/gizle */}
              {(() => {
                const selectedLocation = locations.find(loc => loc.id === parseInt(formData.location_id));
                const showServiceType = selectedLocation?.type === 'both';

                return (
                  <>
                    {showServiceType && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Hizmet Türü *
                        </label>
                        <FormSelect
                          name="service_type"
                          value={formData.service_type}
                          onChange={handleInputChange}
                          options={[
                            { value: 'valet', label: 'Vale Hizmeti' },
                            { value: 'wash', label: 'Yıkama' },
                            { value: 'both', label: 'Vale + Yıkama' }
                          ]}
                          disabled={isLoading}
                        />
                      </div>
                    )}

                    <div className={showServiceType ? '' : 'md:col-span-2'}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lokasyon *
                      </label>
                      <FormSelect
                        name="location_id"
                        value={formData.location_id}
                        onChange={handleInputChange}
                        options={locations.map(location => ({ value: location.id.toString(), label: location.name }))}
                        placeholder="Lokasyon seçin"
                        error={errors.location_id}
                        disabled={isLoading || locations.length === 1}
                      />
                      {errors.location_id && (
                        <p className="text-red-500 text-sm mt-1">{errors.location_id}</p>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Payment Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Check className="w-4 h-4 inline mr-2 text-[#7a2c2c]" />
                Ödeme Durumu
              </label>
              <FormSelect
                name="payment_status"
                value={formData.payment_status}
                onChange={handleInputChange}
                options={[
                  { value: 'pending', label: 'Ödeme Bekliyor' },
                  { value: 'paid', label: 'Ödeme Tamamlandı' },
                  { value: 'subscription', label: 'Abone' }
                ]}
                disabled={isLoading}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notlar
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="form-input"
                placeholder="İsteğe bağlı notlar..."
                rows={3}
                maxLength={300}
                disabled={isLoading}
              />
            </div>

            {/* Park Yeri */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-2 text-[#7a2c2c]" />
                Park Yeri (Opsiyonel)
              </label>
              <input
                type="text"
                name="parking_spot"
                value={formData.parking_spot}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Örn: A-15, Kat 2, Blok C-12"
                maxLength={50}
                disabled={isLoading}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VehicleForm;